import React from 'react';
import HomeArea from './HomeArea';
import BoardArm from './BoardArm';
import BoardCenter from './BoardCenter';
import { useBoardState, BoardGameState } from '../../hooks/useBoardState';
import { useBoardInteractions } from '../../hooks/useBoardInteractions';
import './LudoBoard.css';

interface LudoBoardProps {
  gameState: BoardGameState;
  onSquareClick: (position: number) => void;
  onPieceClick?: (pieceIndex: number) => void;
  validMoves?: number[];
  isPositionSafeForPiece?: (
    position: number,
    pieceColor: 'red' | 'blue' | 'green' | 'yellow'
  ) => boolean;
  celebratingPiece?: {
    playerId: string;
    pieceId: string;
    color: string;
  } | null;
}

const LudoBoard: React.FC<LudoBoardProps> = ({
  gameState,
  onSquareClick,
  onPieceClick,
  validMoves = [],
  isPositionSafeForPiece,
  celebratingPiece,
}) => {
  const { currentPlayer } = useBoardState(gameState, validMoves);
  const { handleHomePieceClick } = useBoardInteractions({
    gameState,
    onSquareClick,
    onPieceClick,
  });

  return (
    <div className="ludo-board-container">
      <div className="board">
        {/* Top Row - Red Home, Up Arm (Green), Green Home */}
        <div className="row">
          {/* Red Home */}
          <HomeArea
            player={gameState.players[0]}
            isCurrentPlayer={currentPlayer?.id === gameState.players[0]?.id}
            validMoves={validMoves}
            onPieceClick={handleHomePieceClick}
          />

          {/* Up Arm (Green) */}
          <BoardArm
            type="up"
            gameState={gameState}
            validMoves={validMoves}
            onSquareClick={onSquareClick}
            onPieceClick={onPieceClick}
          />

          {/* Green Home */}
          <HomeArea
            player={gameState.players[1]}
            isCurrentPlayer={currentPlayer?.id === gameState.players[1]?.id}
            validMoves={validMoves}
            onPieceClick={handleHomePieceClick}
          />
        </div>

        {/* Middle Row - Left Arm (Red), Center, Right Arm (Yellow) */}
        <div className="row">
          {/* Left Arm (Red) */}
          <BoardArm
            type="left"
            gameState={gameState}
            validMoves={validMoves}
            onSquareClick={onSquareClick}
            onPieceClick={onPieceClick}
          />

          {/* Center Destination */}
          <BoardCenter celebratingPiece={celebratingPiece} />

          {/* Right Arm (Yellow) */}
          <BoardArm
            type="right"
            gameState={gameState}
            validMoves={validMoves}
            onSquareClick={onSquareClick}
            onPieceClick={onPieceClick}
          />
        </div>

        {/* Bottom Row - Blue Home, Down Arm (Blue), Yellow Home */}
        <div className="row">
          {/* Blue Home */}
          <HomeArea
            player={gameState.players[3]}
            isCurrentPlayer={currentPlayer?.id === gameState.players[3]?.id}
            validMoves={validMoves}
            onPieceClick={handleHomePieceClick}
          />

          {/* Down Arm (Blue) */}
          <BoardArm
            type="down"
            gameState={gameState}
            validMoves={validMoves}
            onSquareClick={onSquareClick}
            onPieceClick={onPieceClick}
          />

          {/* Yellow Home */}
          <HomeArea
            player={gameState.players[2]}
            isCurrentPlayer={currentPlayer?.id === gameState.players[2]?.id}
            validMoves={validMoves}
            onPieceClick={handleHomePieceClick}
          />
        </div>
      </div>
    </div>
  );
};

export default LudoBoard;
